import { useState, useEffect, useMemo, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { EmptyStateAnalytics } from "./EmptyStateAnalytics";
import { PostingBarChart } from "./PostingBarChart";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle, Users, FileText, Eye, DollarSign, Percent, Calendar, X } from "lucide-react";
import { CustomDataTable } from "@/components/table/CustomDataTable";
import { TableColumnManager } from "@/components/table/TableColumnManager";
import { useTableSettings } from "@/hooks/useTableSettings";
import { StyledBadge } from "@/components/kol/StyledBadge";
import { CreatorInfo } from "@/components/kol/CreatorInfo";
import { useTheme } from "@/contexts/ThemeContext";
import { Badge } from "@/components/ui/badge";
import { EngagementBarChart } from "./EngagementBarChart";
import { AvgViewBarChart } from "./AvgViewBarChart";
import { ExpenseBarChart } from "./ExpenseBarChart";
import { CpvBarChart } from "./CpvBarChart";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TopProgressBar } from "@/components/ui/top-progress-bar";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { KolAnalyticsHierarchicalRow } from "./KolAnalyticsHierarchicalRow";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useTableSorting } from "@/hooks/useTableSorting";
import { TablePagination } from "@/components/ui/table-pagination";

interface KolAnalyticsData {
  kol_db_id: string;
  kol_name: string;
  social_media_handler?: string | null;
  platform?: string | null;
  profile_image_url?: string | null;
  content_count: number;
  total_expense: number;
  total_views: number;
  languages: string[];
  actual_cpv: number | string;
  avg_views_per_post: number | string;
  post_type?: string | null;  
  type?: string | null;
  tier?: string | null;
  posting_year?: number | null;
  pic_id?: any;
  region?: string | null;
  earliest_post_date?: string | null;
  latest_post_date?: string | null;
  last_updated?: string | null;
  parent_id?: string | null;
}

interface HierarchicalKolAnalyticsData extends KolAnalyticsData {
  children?: HierarchicalKolAnalyticsData[];
}

interface DataQualityStats {
  totalRecords: number;
  invalidPostingDate: number;
  missingPostingDate: number;
  invalidLanguage: number;
  missingLanguage: number;
  missingKolDbId: number;
  validRecords: number;
  dateOutOfRange: number;
  missingExpense: number;
  missingContentViews: number;
}

interface FetchProgress {
  current: number;
  total: number;
  estimatedTotal?: number;
}

interface PaginatedData {
  data: KolAnalyticsData[];
  totalCount: number;
}

interface SummaryData {
  totalPosting: number;
  totalEngagement: number;
  totalExpense: number;
  avgViewPerPost: number;
  avgCpv: number;
}

// Utility function to normalize language codes
const normalizeLanguage = (language: string | null): string | null => {
  if (!language) return null;
  
  const lang = language.toLowerCase().trim();
  const languageMap: { [key: string]: string } = {
    'chinese': 'CN',
    'turkish': 'TR',
    'english': 'EN',
    'japanese': 'JP',
    'korean': 'KR',
    'indonesia': 'ID',
    'spanish': 'ES',
    'vietnamese': 'VN',
    'russian': 'RU',
    'indian': 'IN',
    'cn': 'CN',
    'tr': 'TR',
    'en': 'EN',
    'jp': 'JP',
    'kr': 'KR',
    'id': 'ID',
    'es': 'ES',
    'vn': 'VN',
    'ru': 'RU',
    'in': 'IN'
  };
  
  return languageMap[lang] || language.toUpperCase();
};

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  if (num === 0) return '0';
  return num.toLocaleString();
};

// Utility function to fetch all kol_tracker data in batches
const fetchAllKolTrackerData = async (): Promise<any[]> => {
  const BATCH_SIZE = 1000;
  let allData: any[] = [];
  let hasMoreData = true;
  let currentBatch = 0;

  while (hasMoreData) {
    const { data: batchData, error } = await supabase
      .from('kol_tracker')
      .select('*')
      .range(currentBatch * BATCH_SIZE, (currentBatch + 1) * BATCH_SIZE - 1)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Batch fetch error:', error);
      throw error;
    }

    if (batchData && batchData.length > 0) {
      allData = [...allData, ...batchData];
      currentBatch++;
      
      if (batchData.length < BATCH_SIZE) {
        hasMoreData = false;
      }
    } else {
      hasMoreData = false;
    }

    // Small delay to prevent overwhelming the database
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  return allData;
};

// Utility function to analyze data quality statistics
const analyzeDataQualityStats = (data: any[]): DataQualityStats => {
  const stats: DataQualityStats = {
    totalRecords: data.length,
    invalidPostingDate: 0,
    missingPostingDate: 0,
    invalidLanguage: 0,
    missingLanguage: 0,
    missingKolDbId: 0,
    validRecords: 0,
    dateOutOfRange: 0,
    missingExpense: 0,
    missingContentViews: 0
  };

  const currentYear = new Date().getFullYear();
  const validYears = [2024, 2025];

  data.forEach(record => {
    // Check posting date
    if (!record.posting_date) {
      stats.missingPostingDate++;
    } else {
      const postingDate = new Date(record.posting_date);
      const postingYear = postingDate.getFullYear();
      if (!validYears.includes(postingYear)) {
        stats.dateOutOfRange++;
      }
      if (isNaN(postingDate.getTime())) {
        stats.invalidPostingDate++;
      }
    }

    // Check language
    if (!record.language) {
      stats.missingLanguage++;
    } else {
      const normalizedLang = normalizeLanguage(record.language);
      if (!normalizedLang) {
        stats.invalidLanguage++;
      }
    }

    // Check KOL DB ID
    if (!record.kol_db_id) {
      stats.missingKolDbId++;
    }

    // Check expense
    if (!record.expense || record.expense === 0) {
      stats.missingExpense++;
    }

    // Check content views
    if (!record.content_views || record.content_views === 0) {
      stats.missingContentViews++;
    }

    // Count valid records
    if (record.posting_date && record.language && record.kol_db_id) {
      stats.validRecords++;
    }
  });

  return stats;
};

// Utility function to calculate summary data
const calculateSummaryData = (data: KolAnalyticsData[]): SummaryData => {
  const totalPosting = data.reduce((sum, kol) => sum + kol.content_count, 0);
  const totalEngagement = data.reduce((sum, kol) => sum + kol.total_views, 0);
  const totalExpense = data.reduce((sum, kol) => sum + kol.total_expense, 0);
  const avgViewPerPost = totalPosting > 0 ? totalEngagement / totalPosting : 0;
  const avgCpv = totalEngagement > 0 ? totalExpense / totalEngagement : 0;

  return {
    totalPosting,
    totalEngagement,
    totalExpense,
    avgViewPerPost,
    avgCpv
  };
};

// New utility function to process hierarchical data
const processKolAnalyticsData = (data: KolAnalyticsData[]): HierarchicalKolAnalyticsData[] => {
  const dataMap = new Map<string, HierarchicalKolAnalyticsData>();
  const rootItems: HierarchicalKolAnalyticsData[] = [];

  // First pass: create all items
  data.forEach(item => {
    dataMap.set(item.kol_db_id, { ...item, children: [] });
  });

  // Second pass: build hierarchy
  data.forEach(item => {
    const currentItem = dataMap.get(item.kol_db_id)!;
    
    if (item.parent_id) {
      const parent = dataMap.get(item.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(currentItem);
      } else {
        // If parent not found, treat as root
        rootItems.push(currentItem);
      }
    } else {
      rootItems.push(currentItem);
    }
  });

  return rootItems;
};

export const KolAnalyticsTable = () => {
  const [paginatedData, setPaginatedData] = useState<PaginatedData>({ data: [], totalCount: 0 });
  const [allData, setAllData] = useState<KolAnalyticsData[]>([]);
  const [hierarchicalData, setHierarchicalData] = useState<HierarchicalKolAnalyticsData[]>([]);
  
  const [summaryData, setSummaryData] = useState<SummaryData>({
    totalPosting: 0,
    totalEngagement: 0,
    totalExpense: 0,
    avgViewPerPost: 0,
    avgCpv: 0
  });
  
  const [dataQualityStats, setDataQualityStats] = useState<DataQualityStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchProgress, setFetchProgress] = useState<FetchProgress | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userAccess, setUserAccess] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  
  const [selectedPic, setSelectedPic] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  const allLanguages = ['CN', 'TR', 'EN', 'JP', 'KR', 'ID', 'ES', 'VN', 'RU', 'IN'];
  const allYears = [2024, 2025];
  
  const columnDefs = [
    { id: "kol_name", header: "KOL" },
    { id: "languages", header: "Languages" },
    { id: "tier", header: "Tier" },
    { id: "post_type", header: "Type" },
    { id: "pic_id", header: "PIC" },
    { id: "content_count", header: "Total Posting" },
    { id: "total_views", header: "Total Engagements" },
    { id: "avg_views_per_post", header: "Avg View/Post" },
    { id: "total_expense", header: "Expense" },
    { id: "actual_cpv", header: "Actual CPV" }
  ];
  
  const tableSettings = useTableSettings('kol-analytics', columnDefs.map(col => col.id));

  useEffect(() => {
    const access = localStorage.getItem('dashboard_user_access');
    const userName = localStorage.getItem('dashboard_user_name');
    
    setUserAccess(access);
    setUserName(userName);
  }, []);

  const processKolAnalyticsDataResults = async (): Promise<{ allData: KolAnalyticsData[], paginatedData: PaginatedData }> => {
    try {
      const hasFullAccess = userAccess === 'full-access';
      
      console.log('Starting comprehensive data processing with date filter:', { 
        dateFrom: dateFrom?.toISOString().split('T')[0] || 'none',
        dateTo: dateTo?.toISOString().split('T')[0] || 'none'
      });

      const BATCH_SIZE = 1000;
      let allKolTrackerData: any[] = [];
      let hasMoreData = true;
      let currentBatch = 0;
      let totalFetched = 0;

      const { count: totalRecords } = await supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true });

      console.log(`Total kol_tracker records: ${totalRecords || 'unknown'}`);
      
      setFetchProgress({
        current: 0,
        total: Math.ceil((totalRecords || 1000) / BATCH_SIZE),
        estimatedTotal: totalRecords || 0
      });

      while (hasMoreData) {
        console.log(`Fetching batch ${currentBatch + 1}...`);
        
        const { data: batchData, error } = await supabase
          .from('kol_tracker')
          .select('*')
          .range(currentBatch * BATCH_SIZE, (currentBatch + 1) * BATCH_SIZE - 1)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Batch fetch error:', error);
          throw error;
        }

        if (batchData && batchData.length > 0) {
          allKolTrackerData = [...allKolTrackerData, ...batchData];
          totalFetched += batchData.length;
          currentBatch++;
          
          setFetchProgress({
            current: currentBatch,
            total: Math.ceil((totalRecords || 1000) / BATCH_SIZE),
            estimatedTotal: totalRecords || 0
          });

          console.log(`Batch ${currentBatch} complete. Total records: ${totalFetched}`);
          
          if (batchData.length < BATCH_SIZE) {
            hasMoreData = false;
          }
        } else {
          hasMoreData = false;
        }

        await new Promise(resolve => setTimeout(resolve, 50));
      }

      console.log(`Completed batch fetching. Total kol_tracker records: ${allKolTrackerData.length}`);

      let filteredTrackerData = allKolTrackerData;
      if (dateFrom || dateTo) {
        const originalCount = filteredTrackerData.length;
        filteredTrackerData = allKolTrackerData.filter(record => {
          if (!record.posting_date) return false;
          
          const postingDate = new Date(record.posting_date);
          let includeRecord = true;
          
          if (dateFrom) {
            includeRecord = includeRecord && postingDate >= dateFrom;
          }
          if (dateTo) {
            includeRecord = includeRecord && postingDate <= dateTo;
          }
          
          return includeRecord;
        });
        
        console.log(`Date filtering: ${originalCount} -> ${filteredTrackerData.length} records`);
      }

      if (!hasFullAccess && userName) {
        const preFilterCount = filteredTrackerData.length;
        filteredTrackerData = filteredTrackerData.filter(record => {
          if (!record.pic) return false;
          const picArray = Array.isArray(record.pic) ? record.pic : [];
          return picArray.includes(userName);
        });
        console.log(`User access filtering: ${preFilterCount} -> ${filteredTrackerData.length} records`);
      }

      const { data: rawKolDbData, error: dbError } = await supabase
        .from('kol_db')
        .select('*');
      
      if (dbError) {
        console.error('Error fetching kol_db:', dbError);
        throw dbError;
      }

      console.log(`Fetched ${rawKolDbData?.length || 0} kol_db records`);

      const kolDbMap = new Map<string, any>();
      rawKolDbData?.forEach(kol => {
        kolDbMap.set(kol.id, kol);
      });

      const kolAnalyticsMap = new Map<string, {
        kol_db_id: string;
        content_count: number;
        total_expense: number;
        total_views: number;
        languages: Set<string>;
        post_types: Map<string, number>;
        earliest_post_date: string | null;
        latest_post_date: string | null;
        last_updated: string | null;
      }>();

      filteredTrackerData.forEach(record => {
        if (!record.kol_db_id) return;

        const kolId = record.kol_db_id;
        
        if (!kolAnalyticsMap.has(kolId)) {
          kolAnalyticsMap.set(kolId, {
            kol_db_id: kolId,
            content_count: 0,
            total_expense: 0,
            total_views: 0,
            languages: new Set(),
            post_types: new Map(),
            earliest_post_date: null,
            latest_post_date: null,
            last_updated: null
          });
        }

        const analytics = kolAnalyticsMap.get(kolId)!;
        
        analytics.content_count += 1;
        analytics.total_expense += record.expense || 0;
        analytics.total_views += record.content_views || 0;
        
        if (record.language) {
          const normalizedLang = normalizeLanguage(record.language);
          if (normalizedLang) {
            analytics.languages.add(normalizedLang);
          }
        }
        
        if (record.post_type) {
          const currentCount = analytics.post_types.get(record.post_type) || 0;
          analytics.post_types.set(record.post_type, currentCount + 1);
        }
        
        if (record.posting_date) {
          if (!analytics.earliest_post_date || record.posting_date < analytics.earliest_post_date) {
            analytics.earliest_post_date = record.posting_date;
          }
          if (!analytics.latest_post_date || record.posting_date > analytics.latest_post_date) {
            analytics.latest_post_date = record.posting_date;
          }
        }
        
        if (!analytics.last_updated || record.updated_at > analytics.last_updated) {
          analytics.last_updated = record.updated_at;
        }
      });

      const transformedData: KolAnalyticsData[] = Array.from(kolAnalyticsMap.values())
        .map(analytics => {
          const kolDbInfo = kolDbMap.get(analytics.kol_db_id);
          
          let mostCommonPostType: string | null = null;
          let maxCount = 0;
          analytics.post_types.forEach((count, type) => {
            if (count > maxCount) {
              maxCount = count;
              mostCommonPostType = type;
            }
          });

          return {
            kol_db_id: analytics.kol_db_id,
            kol_name: kolDbInfo?.creator_name || 'Unknown',
            social_media_handler: kolDbInfo?.social_media_handler || null,
            platform: kolDbInfo?.social_media_type || null,
            profile_image_url: kolDbInfo?.profile_image_url || null,
            content_count: analytics.content_count,
            total_expense: analytics.total_expense,
            total_views: analytics.total_views,
            languages: Array.from(analytics.languages),
            actual_cpv: analytics.total_views > 0 ? analytics.total_expense / analytics.total_views : 0,
            avg_views_per_post: analytics.content_count > 0 ? analytics.total_views / analytics.content_count : 0,
            post_type: mostCommonPostType,
            type: kolDbInfo?.kol_type || null,
            tier: kolDbInfo?.tier || null,
            posting_year: analytics.earliest_post_date ? new Date(analytics.earliest_post_date).getFullYear() : null,
            pic_id: kolDbInfo?.pic_id || null,
            region: kolDbInfo?.region || null,
            earliest_post_date: analytics.earliest_post_date,
            latest_post_date: analytics.latest_post_date,
            last_updated: analytics.last_updated,
            parent_id: kolDbInfo?.parent_id || null
          };
        })
        .filter(kol => kol.content_count > 0)
        .sort((a, b) => b.content_count - a.content_count);

      console.log(`Final processed data: ${transformedData.length} KOL records`);
      
      const totalPosts = transformedData.reduce((sum, kol) => sum + kol.content_count, 0);
      const totalViews = transformedData.reduce((sum, kol) => sum + kol.total_views, 0);
      const totalExpense = transformedData.reduce((sum, kol) => sum + kol.total_expense, 0);
      
      console.log('Summary metrics:', {
        totalKols: transformedData.length,
        totalPosts,
        totalViews,
        totalExpense,
        avgCpv: totalViews > 0 ? totalExpense / totalViews : 0
      });

      const firstPageData = transformedData.slice(0, pageSize);
      
      return {
        allData: transformedData,
        paginatedData: {
          data: firstPageData,
          totalCount: transformedData.length
        }
      };
    } catch (err) {
      console.error(`Failed to process KOL analytics data:`, err);
      throw err;
    }
  };

  const availablePics = useMemo(() => {
    const picSet = new Set<string>();
    allData.forEach(kol => {
      if (kol.pic_id && Array.isArray(kol.pic_id)) {
        kol.pic_id.forEach(pic => {
          if (typeof pic === 'string' && pic.trim()) {
            picSet.add(pic.trim());
          }
        });
      }
    });
    return Array.from(picSet).sort();
  }, [allData]);

  const availableLanguages = useMemo(() => {
    const languageSet = new Set<string>();
    allData.forEach(kol => {
      if (kol.languages && Array.isArray(kol.languages)) {
        kol.languages.forEach(lang => {
          if (typeof lang === 'string' && lang.trim()) {
            languageSet.add(lang.trim());
          }
        });
      }
    });
    return Array.from(languageSet).sort();
  }, [allData]);

  const getFilteredAndPaginatedData = useCallback((allData: KolAnalyticsData[], searchTerm: string, page: number, size: number, picFilter: string, languageFilter: string) => {
    let filteredData = allData;
    
    // Apply search filter
    if (searchTerm.trim() !== "") {
      filteredData = filteredData.filter(item => 
        item.kol_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply PIC filter
    if (picFilter.trim() !== "") {
      filteredData = filteredData.filter(item => {
        if (!item.pic_id || !Array.isArray(item.pic_id)) return false;
        return item.pic_id.some(pic => 
          typeof pic === 'string' && pic.trim().toLowerCase().includes(picFilter.toLowerCase())
        );
      });
    }

    // Apply language filter
    if (languageFilter.trim() !== "") {
      filteredData = filteredData.filter(item => {
        if (!item.languages || !Array.isArray(item.languages)) return false;
        return item.languages.some(lang => 
          typeof lang === 'string' && lang.trim().toLowerCase() === languageFilter.toLowerCase()
        );
      });
    }

    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const paginatedResults = filteredData.slice(startIndex, endIndex);

    return {
      data: paginatedResults,
      totalCount: filteredData.length,
      filteredData: filteredData
    };
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    
    const result = getFilteredAndPaginatedData(allData, searchQuery, newPage, pageSize, selectedPic, selectedLanguage);
    setPaginatedData({ data: result.data, totalCount: result.totalCount });
  }, [allData, searchQuery, pageSize, selectedPic, selectedLanguage, getFilteredAndPaginatedData]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    
    const result = getFilteredAndPaginatedData(allData, searchQuery, 1, newSize, selectedPic, selectedLanguage);
    setPaginatedData({ data: result.data, totalCount: result.totalCount });
  }, [allData, searchQuery, selectedPic, selectedLanguage, getFilteredAndPaginatedData]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userAccess || !userName) return;
      
      setIsLoading(true);
      setFetchError(null);
      
      try {
        console.log('Starting client-side data processing...', { dateFrom, dateTo });
        
        if (!dataQualityStats) {
          const kolTrackerData = await fetchAllKolTrackerData();
          const qualityStats = analyzeDataQualityStats(kolTrackerData);
          setDataQualityStats(qualityStats);
        }
        
        const dataResult = await processKolAnalyticsDataResults();
        
        setAllData(dataResult.allData);
        
        // Process hierarchical data
        const hierarchical = processKolAnalyticsData(dataResult.allData);
        setHierarchicalData(hierarchical);
        
        setPaginatedData(dataResult.paginatedData);
        
        setSummaryData(calculateSummaryData(dataResult.allData));
        
        setCurrentPage(1);
        
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
        
        console.log('Client-side data processing completed successfully');
        
      } catch (err) {
        console.error("Failed to process KOL analytics data:", err);
        setFetchError("Failed to load analytics data");
        toast({
          title: "Failed to load analytics data",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setFetchProgress(null);
      }
    };
    
    fetchData();
  }, [userAccess, userName, toast, pageSize, dateFrom, dateTo]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      
      const result = getFilteredAndPaginatedData(allData, searchQuery, 1, pageSize, selectedPic, selectedLanguage);
      setPaginatedData({ data: result.data, totalCount: result.totalCount });
      
      // Update hierarchical data based on filtered results
      const hierarchical = processKolAnalyticsData(result.filteredData);
      setHierarchicalData(hierarchical);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, pageSize, allData, selectedPic, selectedLanguage, getFilteredAndPaginatedData]);

  const currentSearchSummary = useMemo(() => {
    if (searchQuery.trim() === "" && selectedPic === "" && selectedLanguage === "") {
      return summaryData;
    } else {
      const result = getFilteredAndPaginatedData(allData, searchQuery, 1, allData.length, selectedPic, selectedLanguage);
      const filteredData = result.filteredData;
      
      const calculatedSummary = calculateSummaryData(filteredData);
      
      return calculatedSummary;
    }
  }, [allData, searchQuery, selectedPic, selectedLanguage, summaryData, getFilteredAndPaginatedData]);

  const maxContentCount = useMemo(() => {
    const getAllValues = (items: HierarchicalKolAnalyticsData[]): number[] => {
      const values: number[] = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          // For parents with children, use total values (parent + children)
          const totalContent = item.content_count + item.children.reduce((sum, child) => sum + child.content_count, 0);
          values.push(totalContent);
          // Also add individual child values for when they're shown separately
          item.children.forEach(child => values.push(child.content_count));
        } else {
          values.push(item.content_count);
        }
      });
      return values;
    };
    return Math.max(...getAllValues(hierarchicalData), 1);
  }, [hierarchicalData]);

  const maxTotalViews = useMemo(() => {
    const getAllValues = (items: HierarchicalKolAnalyticsData[]): number[] => {
      const values: number[] = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const totalViews = item.total_views + item.children.reduce((sum, child) => sum + child.total_views, 0);
          values.push(totalViews);
          item.children.forEach(child => values.push(child.total_views));
        } else {
          values.push(item.total_views);
        }
      });
      return values;
    };
    return Math.max(...getAllValues(hierarchicalData), 1);
  }, [hierarchicalData]);

  const maxAvgViews = useMemo(() => {
    const getAllValues = (items: HierarchicalKolAnalyticsData[]): number[] => {
      const values: number[] = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const totalViews = item.total_views + item.children.reduce((sum, child) => sum + child.total_views, 0);
          const totalContent = item.content_count + item.children.reduce((sum, child) => sum + child.content_count, 0);
          const avgViews = totalContent > 0 ? totalViews / totalContent : 0;
          values.push(avgViews);
          item.children.forEach(child => {
            const childAvg = typeof child.avg_views_per_post === 'number' ? child.avg_views_per_post : 0;
            values.push(childAvg);
          });
        } else {
          const itemAvg = typeof item.avg_views_per_post === 'number' ? item.avg_views_per_post : 0;
          values.push(itemAvg);
        }
      });
      return values;
    };
    return Math.max(...getAllValues(hierarchicalData), 1);
  }, [hierarchicalData]);

  const maxExpense = useMemo(() => {
    const getAllValues = (items: HierarchicalKolAnalyticsData[]): number[] => {
      const values: number[] = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const totalExpense = item.total_expense + item.children.reduce((sum, child) => sum + child.total_expense, 0);
          values.push(totalExpense);
          item.children.forEach(child => values.push(child.total_expense));
        } else {
          values.push(item.total_expense);
        }
      });
      return values;
    };
    return Math.max(...getAllValues(hierarchicalData), 1);
  }, [hierarchicalData]);

  const maxCpv = useMemo(() => {
    const getAllValues = (items: HierarchicalKolAnalyticsData[]): number[] => {
      const values: number[] = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const totalViews = item.total_views + item.children.reduce((sum, child) => sum + child.total_views, 0);
          const totalExpense = item.total_expense + item.children.reduce((sum, child) => sum + child.total_expense, 0);
          const cpv = totalViews > 0 ? totalExpense / totalViews : 0;
          values.push(cpv);
          item.children.forEach(child => {
            const childCpv = typeof child.actual_cpv === 'number' ? child.actual_cpv : 0;
            values.push(childCpv);
          });
        } else {
          const itemCpv = typeof item.actual_cpv === 'number' ? item.actual_cpv : 0;
          values.push(itemCpv);
        }
      });
      return values;
    };
    return Math.max(...getAllValues(hierarchicalData), 1);
  }, [hierarchicalData]);

  const clearDateFilter = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedPic("");
    setSelectedLanguage("");
    setDateFrom(null);
    setDateTo(null);
  };

  const hasActiveFilters = searchQuery || selectedPic || selectedLanguage || dateFrom || dateTo;

  const { sortState, handleSort, sortedData } = useTableSorting({
    data: hierarchicalData,
    defaultSort: { column: null, direction: null }
  });

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedSortedData = sortedData.slice(startIndex, endIndex);

  return (
    <>
      {fetchProgress && (
        <TopProgressBar
          isVisible={true}
          current={fetchProgress.current}
          total={fetchProgress.total}
          title={`Processing KOL Analytics Data (${fetchProgress.estimatedTotal || 'Unknown'} total records)`}
        />
      )}

      <Card className="dashboard-card bg-transparent border-dashboard-border h-full">
        <CardHeader className={`border-b ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'} pb-3`}>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative w-full md:w-64">
                  <Search className={`absolute left-2 top-2.5 h-4 w-4 ${isLightMode ? 'text-gray-500' : 'text-[#8E9196]'}`} />
                  <Input
                    placeholder="Search KOL"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-8 ${isLightMode 
                      ? 'bg-white border-gray-200 text-gray-800 placeholder:text-gray-500 hover:border-gray-300 focus:border-gray-400' 
                      : 'bg-[#2A2A2A] border-[#4A4A4A]'}`}
                  />
                </div>

                <div className="w-48">
                  <SearchableSelect
                    options={availablePics}
                    value={selectedPic}
                    onChange={setSelectedPic}
                    placeholder="Filter by PIC"
                    clearable
                    badgeVariant="blue"
                  />
                </div>

                <div className="w-48">
                  <SearchableSelect
                    options={availableLanguages}
                    value={selectedLanguage}
                    onChange={setSelectedLanguage}
                    placeholder="Filter by Language"
                    clearable
                    badgeVariant="green"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <DateRangePicker
                    from={dateFrom}
                    to={dateTo}
                    onFromChange={setDateFrom}
                    onToChange={setDateTo}
                  />
                  
                  {(dateFrom || dateTo) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearDateFilter}
                      className="whitespace-nowrap"
                    >
                      Clear Dates
                    </Button>
                  )}

                  {hasActiveFilters && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearAllFilters}
                      className="whitespace-nowrap flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
              
              <TableColumnManager
                columns={columnDefs}
                columnVisibility={tableSettings.columnVisibility}
                onColumnVisibilityChange={tableSettings.updateColumnVisibility}
                onResetColumns={tableSettings.resetSettings}
              />
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Active Filters:
                </span>
                {selectedPic && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    PIC: {selectedPic}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-red-500" 
                      onClick={() => setSelectedPic("")}
                    />
                  </Badge>
                )}
                {selectedLanguage && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    Language: {selectedLanguage}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-red-500" 
                      onClick={() => setSelectedLanguage("")}
                    />
                  </Badge>
                )}
                {dateFrom && (
                  <Badge variant="secondary" className="text-xs">
                    From: {dateFrom.toLocaleDateString()}
                  </Badge>
                )}
                {dateTo && (
                  <Badge variant="secondary" className="text-xs">
                    To: {dateTo.toLocaleDateString()}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="min-h-[400px] w-full relative overflow-x-auto">
              <TableLoadingOverlay isLoading={true} />
            </div>
          ) : (
            <div className="space-y-4">
              {dataQualityStats && (
                <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-sm">
                    <div className="font-semibold text-orange-800 dark:text-orange-200 mb-2">
                      Data Quality Analysis - Total Fetched: {formatNumber(dataQualityStats.totalRecords)} records
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="font-medium">Valid Records:</span> {formatNumber(dataQualityStats.validRecords)}
                      </div>
                      <div>
                        <span className="font-medium">Missing Posting Date:</span> {formatNumber(dataQualityStats.missingPostingDate)}
                      </div>
                      <div>
                        <span className="font-medium">Invalid Posting Date:</span> {formatNumber(dataQualityStats.invalidPostingDate)}
                      </div>
                      <div>
                        <span className="font-medium">Date Out of Range:</span> {formatNumber(dataQualityStats.dateOutOfRange)}
                      </div>
                      <div>
                        <span className="font-medium">Missing Language:</span> {formatNumber(dataQualityStats.missingLanguage)}
                      </div>
                      <div>
                        <span className="font-medium">Invalid Language:</span> {formatNumber(dataQualityStats.invalidLanguage)}
                      </div>
                      <div>
                        <span className="font-medium">Missing KOL DB ID:</span> {formatNumber(dataQualityStats.missingKolDbId)}
                      </div>
                      <div>
                        <span className="font-medium">Missing Expense:</span> {formatNumber(dataQualityStats.missingExpense)}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium opacity-90 mb-1">
                          TOTAL POSTING
                        </div>
                        <div className="text-2xl font-bold">
                          {formatNumber(currentSearchSummary.totalPosting)}
                          <div className="text-xs opacity-80 mt-1">
                            {formatNumber(paginatedData.totalCount)} records
                          </div>
                        </div>
                      </div>
                      <FileText className="h-8 w-8 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500 to-green-600 border-0 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium opacity-90 mb-1">
                          TOTAL ENGAGEMENT
                        </div>
                        <div className="text-2xl font-bold">
                          {formatNumber(currentSearchSummary.totalEngagement)}
                        </div>
                      </div>
                      <Eye className="h-8 w-8 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 border-0 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium opacity-90 mb-1">
                          AVG VIEW/POST
                        </div>
                        <div className="text-2xl font-bold">
                          {formatNumber(Math.round(currentSearchSummary.avgViewPerPost))}
                        </div>
                      </div>
                      <Eye className="h-8 w-8 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium opacity-90 mb-1">
                          TOTAL EXPENSE
                        </div>
                        <div className="text-2xl font-bold">
                          ${formatNumber(currentSearchSummary.totalExpense)}
                        </div>
                      </div>
                      <DollarSign className="h-8 w-8 opacity-80" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium opacity-90 mb-1">
                          AVG ACTUAL CPV
                        </div>
                        <div className="text-2xl font-bold">
                          ${currentSearchSummary.avgCpv.toFixed(4)}
                        </div>
                      </div>
                      <Percent className="h-8 w-8 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {hierarchicalData.length === 0 ? (
                <EmptyStateAnalytics message="No KOL data available for the selected filters" />
              ) : (
                <div className="space-y-4">
                  <div className="min-h-[400px] w-full relative overflow-x-auto">
                    <div className="w-full relative">
                      <table className="w-full border-collapse bg-background">
                        <thead className="sticky top-0 z-10 bg-muted/30">
                          <tr className="border-b border-border/50">
                            <th className="w-[40px] px-2 py-3 text-left">
                              {/* Expand/Collapse column */}
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="kol_name"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                KOL
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="languages"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Languages
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="tier"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Tier
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="post_type"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Type
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="pic_id"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                PIC
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="content_count"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Total Posting
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="total_views"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Total Engagements
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="avg_views_per_post"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Avg View/Post
                              </SortableHeader>
                            </th>
                            <th className="border-r border-border/50">
                              <SortableHeader
                                column="total_expense"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Total Expense
                              </SortableHeader>
                            </th>
                            <th>
                              <SortableHeader
                                column="actual_cpv"
                                currentSort={sortState}
                                onSort={handleSort}
                                className="px-3 py-3"
                              >
                                Actual CPV
                              </SortableHeader>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedSortedData.map((row) => (
                            <KolAnalyticsHierarchicalRow
                              key={row.kol_db_id}
                              data={row}
                              level={0}
                              maxContentCount={maxContentCount}
                              maxTotalViews={maxTotalViews}
                              maxAvgViews={maxAvgViews}
                              maxExpense={maxExpense}
                              maxCpv={maxCpv}
                              isAnimating={isAnimating}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <TablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    totalItems={sortedData.length}
                    showPageSizeSelect={true}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
